import React, { useEffect, useState } from 'react';
import { StaffCard } from '../components/StaffCard';
import { api } from '../services/api';
import { Staff } from '../types';
import { Users, Sparkles, Briefcase, HeartHandshake } from 'lucide-react';
import { Link } from 'react-router-dom';

export const StaffPage: React.FC = () => {
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const data = await api.getStaff();
        setStaff(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStaff();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16 pb-24">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-950/60 border border-purple-500/30 text-purple-300 text-xs font-semibold">
          <Users className="w-3.5 h-3.5 text-purple-400" />
          <span>The Human Engine</span>
        </div>
        <h1 className="text-4xl font-black text-white tracking-tight">Our Team & Staff</h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          Meet the minds behind GSR STUDIO. We are a collective of world-builders, graphics engineers, composers, and storytellers united by a passion for unforgettable interactive adventures.
        </p>
      </div>

      {/* Staff Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-80 rounded-2xl bg-slate-900/50 animate-pulse border border-slate-800" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {staff.map((member) => (
            <StaffCard key={member.id} member={member} />
          ))}
        </div>
      )}

      {/* Studio Culture & Values */}
      <section className="bg-slate-900/40 rounded-3xl border border-slate-800 p-8 sm:p-12 space-y-8">
        <div className="text-center max-w-xl mx-auto space-y-2">
          <h2 className="text-xs uppercase tracking-widest text-cyan-400 font-bold">Studio Culture</h2>
          <h3 className="text-2xl font-bold text-white">How We Build Together</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-3">
            <Sparkles className="w-6 h-6 text-cyan-400" />
            <h4 className="text-base font-bold text-white">Autonomy & Craft</h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              Every team member owns their craft. No micromanagement; just high trust, rapid prototyping, and shared pride in finished work.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-3">
            <HeartHandshake className="w-6 h-6 text-fuchsia-400" />
            <h4 className="text-base font-bold text-white">Anti-Crunch Policy</h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              Great games come from rested creators. We practice realistic roadmap planning, flexible remote schedules, and zero mandatory overtime.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-3">
            <Briefcase className="w-6 h-6 text-emerald-400" />
            <h4 className="text-base font-bold text-white">Open Career Paths</h4>
            <p className="text-slate-400 text-xs leading-relaxed">
              We mentor and promote internally. Whether switching from gameplay code to tools or 2D concept to 3D tech art, we fund your growth.
            </p>
          </div>
        </div>
      </section>

      {/* Careers Banner */}
      <div className="rounded-2xl bg-gradient-to-r from-purple-900/40 to-slate-900 border border-purple-500/30 p-8 text-center space-y-4">
        <h3 className="text-2xl font-bold text-white">Want to join GSR STUDIO?</h3>
        <p className="text-slate-300 text-xs max-w-lg mx-auto">
          We are currently reviewing applications for Senior Graphics Engineers, Level Designers, and Combat Animators.
        </p>
        <div>
          <Link
            to="/contact"
            className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-400 hover:to-cyan-400 text-slate-950 font-bold text-xs shadow-lg transition"
          >
            Submit Application / Portfolio
          </Link>
        </div>
      </div>
    </div>
  );
};
